/* Contact · /contact — one form, two-way email, no chatbot tunnel. */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, MessageSquare, Briefcase, Building2, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import api from "../lib/api";
import { toast } from "sonner";
import MarketingMobileNav from "../components/MarketingMobileNav";

const TOPICS = [
  { id: "sales",        label: "I'm a seller — show me the platform",    icon: Briefcase, mailto: "hello@verqo.com" },
  { id: "buyer",        label: "I'm a buyer — how do I get credit?",     icon: Building2, mailto: "hello@verqo.com" },
  { id: "support",      label: "I'm a customer — I need support",        icon: MessageSquare, mailto: "support@verqo.com" },
  { id: "partnership",  label: "I want to partner with verqo",           icon: ArrowRight, mailto: "partners@verqo.com" },
  { id: "press",        label: "I'm with the press / an investor",       icon: Mail, mailto: "hello@verqo.com" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialTopic = params.get("topic") || "sales";
  const [topic, setTopic] = useState(TOPICS.find(t => t.id === initialTopic) ? initialTopic : "sales");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Contact · verqo"; window.scrollTo(0, 0); }, []);

  const current = useMemo(() => TOPICS.find(t => t.id === topic) || TOPICS[0], [topic]);
  const canSubmit = name.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && message.trim().length >= 12 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      // Best-effort POST to the public contact endpoint. If the endpoint
      // doesn't exist yet we still fall back to a mailto: so the message
      // doesn't get lost.
      try {
        await api.post("/public/contact", {
          topic, name: name.trim(), email: email.trim(),
          company: company.trim() || null, message: message.trim(),
        });
      } catch {
        // Silent fallback — open the user's mail client with a prefilled body.
        const subj = encodeURIComponent(`[${current.label}] ${name}`);
        const body = encodeURIComponent(
          `From: ${name} <${email}>\n` +
          (company ? `Company: ${company}\n` : ``) +
          `Topic: ${current.label}\n\n${message}`
        );
        window.location.href = `mailto:${current.mailto}?subject=${subj}&body=${body}`;
      }
      setSent(true);
      toast.success("Message sent — we'll get back to you within a few hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Please email us directly at hello@verqo.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7f5] text-slate-900">
      <header className="px-6 md:px-12 max-w-[1280px] mx-auto pt-8 pb-4 flex items-center justify-between" data-testid="contact-header">
        <Link to="/" className="inline-flex items-center"><VerqoLockup size="sm" /></Link>
        <nav className="hidden lg:flex items-center gap-6 text-[12px] text-slate-600">
          <Link to="/" className="hover:text-slate-900">For sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">For buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
        </nav>
        <MarketingMobileNav />
      </header>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pt-16 md:pt-24 pb-12" data-testid="contact-hero">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-700 mb-5">
          <span className="w-2 h-2 rounded-full bg-lime-500"/>Contact
        </div>
        <h1 className="text-[32px] sm:text-[40px] md:text-[60px] leading-[1.04] sm:leading-[1.0] md:leading-[0.98] tracking-tight font-semibold">
          Real humans. <br/><span className="text-slate-500">Real replies.</span>
        </h1>
        <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
          No chatbot tunnel. Every message goes to a person on our team and we
          answer within a few hours during business days.
        </p>
      </section>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pb-24" data-testid="contact-form-section">
        {sent ? (
          <div className="rounded-3xl bg-white border border-slate-200 p-10 md:p-14 text-center" data-testid="contact-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-lime-100 grid place-items-center">
              <CheckCircle2 className="w-7 h-7 text-lime-700"/>
            </div>
            <h2 className="mt-6 text-[22px] md:text-[26px] font-semibold tracking-tight">Message received.</h2>
            <p className="mt-3 max-w-md mx-auto text-[14.5px] text-slate-600 leading-relaxed">
              We'll be in touch within a few hours during business days.
              If it's urgent and you don't hear back, email <a href="mailto:hello@verqo.com" className="text-slate-900 underline">hello@verqo.com</a> directly.
            </p>
            <button
              onClick={() => { setSent(false); setName(""); setEmail(""); setCompany(""); setMessage(""); }}
              className="mt-7 text-[12px] uppercase tracking-widest text-slate-500 hover:text-slate-900"
              data-testid="contact-send-another"
            >
              Send another →
            </button>
          </div>
        ) : (
        <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-10">
          {/* Topic picker */}
          <div className="mb-6">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 mb-3">What's this about?</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {TOPICS.map(t => {
                const Icon = t.icon;
                const active = t.id === topic;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTopic(t.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-[13px] transition-all ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`contact-topic-${t.id}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${active ? "text-lime-300" : "text-slate-400"}`}/>
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Your name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-slate-400"
                  placeholder="Jane Doe" data-testid="contact-name"/>
              </div>
              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Work email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-slate-400"
                  placeholder="you@company.com" data-testid="contact-email"/>
              </div>
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Company (optional)</label>
              <input value={company} onChange={e => setCompany(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-slate-400"
                placeholder="Your company" data-testid="contact-company"/>
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-slate-400 resize-y"
                placeholder="Tell us what you're looking for…" data-testid="contact-message"/>
            </div>
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <button type="submit" disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-900 text-white text-[13.5px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="contact-submit">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Sending…</> : <>Send message <ArrowRight className="w-4 h-4"/></>}
              </button>
              <span className="text-[11.5px] text-slate-500">
                Or email <a href={`mailto:${current.mailto}`} className="text-slate-700 underline">{current.mailto}</a> directly.
              </span>
            </div>
          </form>
        </div>
        )}
      </section>

      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-10 border-t border-slate-200 flex flex-wrap gap-4 items-center justify-between text-[11px] text-slate-500" data-testid="contact-footer">
        <div className="flex items-center gap-3"><VerqoLockup size="sm" /><span>· Wholesale, instant.</span></div>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-900">Sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">Buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/faq" className="hover:text-slate-900">FAQ</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </div>
        <div>© 2026 verqo</div>
      </footer>
    </div>
  );
}
